import { ColumnDef } from '@tanstack/react-table'
import { Check } from 'iconoir-react'
import { DateComponent } from 'app/components/DateComponent'
import { LazyLocationCard } from 'app/components/shared/LazyLocationCard'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import {
  DateRangeOperator,
  FilterValueType,
  addDefaultFilterProperties,
  type FilterableColumn,
  type PredefinedFilter,
} from 'app/filter.utils'
import type { AppointmentRow } from 'app/types/appointment-row'
import type { ProviderDirectory } from 'app/types/provider-directory'
import { APPOINTMENT_STATUSES, appointmentStartLabel, appointmentStatusLabel } from 'lib/appointmentsPage'
import { isAppointmentConfirmed } from 'lib/appointmentConfirmed'
import { formatCurrency } from 'lib/formatCurrency'

const STATUS_COLOR: Readonly<Record<string, BadgeColor>> = {
  Scheduled: `blue`,
  Completed: `green`,
  Broken: `red`,
  Cancelled: `red`,
}

const CELL_CLASS = `flex h-full items-center px-2 text-default`

export function appointmentColumns({
  onOpenPatient,
  providers,
  withYear,
  today,
}: {
  onOpenPatient: (contactId: string) => void
  providers: ProviderDirectory
  withYear: boolean
  today: Date
}): ColumnDef<AppointmentRow, any>[] {
  return [
    {
      id: `patientName`,
      accessorFn: (row) => row.patient?.name,
      header: `Patient`,
      enableSorting: false,
      enableHiding: false,
      size: 220,
      meta: {
        defaultVisible: true,
        pinned: true,
        ControlledComponent: (row) => {
          if (row?.patient === undefined) return <div className={CELL_CLASS} />
          if (row.patient === null)
            return (
              <div className={CELL_CLASS}>
                <span className="text-subtle">Unknown patient</span>
              </div>
            )
          const { contactId, name } = row.patient
          return (
            <AnchorCell
              href={`/appointments?drawer=${contactId}`}
              tooltipMessage={name}
              clickHandler={() => onOpenPatient(contactId)}
            >
              <span className="font-medium text-strong">{name}</span>
            </AnchorCell>
          )
        },
      },
    },
    {
      id: `date`,
      accessorFn: (row) => appointmentStartLabel(row, { withYear, today }),
      header: `Date`,
      enableSorting: true,
      enableHiding: false,
      size: 220,
      meta: {
        defaultVisible: true,
        pinned: true,
        sortKey: `date,time`,
        alwaysSorted: true,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>{row && appointmentStartLabel(row, { withYear, today })}</div>
        ),
      },
    },
    {
      id: `status`,
      accessorFn: (row) => appointmentStatusLabel(row.status),
      header: `Status`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        sortKey: `status`,
        ControlledComponent: (row) =>
          row ? (
            <div className={`${CELL_CLASS} gap-1`}>
              <Badge color={STATUS_COLOR[row.status ?? ``] ?? `gray`} label={appointmentStatusLabel(row.status)} />
              {isAppointmentConfirmed(row) && <Badge color="green" icon={<Check />} label="Confirmed" />}
            </div>
          ) : (
            <div className={CELL_CLASS} />
          ),
      },
    },
    {
      id: `type`,
      accessorKey: `type`,
      header: `Type`,
      enableSorting: true,
      meta: { defaultVisible: true, sortKey: `type` },
    },
    {
      id: `providerId`,
      accessorFn: (row) => providers.nameAt(row.locationId, row.providerId),
      header: `Provider`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        sortKey: `providerId`,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>{providers.nameAt(row?.locationId, row?.providerId)}</div>
        ),
      },
    },
    {
      id: `locationId`,
      accessorFn: (row) => row.locationId,
      header: `Location`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        sortKey: `locationId`,
        ControlledComponent: (row) => <LazyLocationCard locationId={row?.locationId} />,
      },
    },
    {
      id: `amount`,
      accessorFn: (row) => row.amount,
      header: `Amount`,
      enableSorting: true,
      meta: {
        defaultVisible: false,
        sortKey: `amount`,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>
            {row?.amount == null ? null : <span className="tabular-nums">{formatCurrency(row.amount)}</span>}
          </div>
        ),
      },
    },
    {
      id: `procedureCodes`,
      accessorFn: (row) => row.procedureCodes?.join(`, `),
      header: `Procedure codes`,
      enableSorting: false,
      meta: { defaultVisible: false },
    },
    {
      id: `duration`,
      accessorFn: (row) => row.duration,
      header: `Duration`,
      enableSorting: true,
      meta: {
        defaultVisible: false,
        sortKey: `duration`,
        ControlledComponent: (row) => <div className={CELL_CLASS}>{row?.duration ? `${row.duration} min` : null}</div>,
      },
    },
    {
      id: `appointmentMadeDate`,
      accessorFn: (row) => row.appointmentMadeDate,
      header: `Booked on`,
      enableSorting: true,
      meta: {
        defaultVisible: false,
        sortKey: `appointmentMadeDate`,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>
            {row?.appointmentMadeDate ? <DateComponent value={row.appointmentMadeDate} dateOnly /> : null}
          </div>
        ),
      },
    },
    {
      id: `timeToAppointment`,
      accessorFn: (row) => row.timeToAppointment,
      header: `Lead time`,
      enableSorting: true,
      meta: {
        defaultVisible: false,
        sortKey: `timeToAppointment`,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>{row?.timeToAppointment == null ? null : `${row.timeToAppointment} days`}</div>
        ),
      },
    },
    {
      id: `source`,
      accessorKey: `source`,
      header: `Source`,
      enableSorting: true,
      meta: { defaultVisible: false, sortKey: `source` },
    },
    {
      id: `note`,
      accessorKey: `note`,
      header: `Note`,
      enableSorting: false,
      meta: {
        defaultVisible: false,
        ControlledComponent: (row) => (
          <div className={CELL_CLASS}>
            <span className="truncate">{row?.note}</span>
          </div>
        ),
      },
    },
  ]
}

export function appointmentFilterableColumns({
  preAppliedFilters,
  providers,
}: {
  preAppliedFilters: PredefinedFilter
  providers: ProviderDirectory
}): FilterableColumn[] {
  const columns: FilterableColumn[] = [
    {
      attribute: `locationId`,
      displayName: `Location`,
      type: FilterValueType.id,
      group: `General`,
      optionsSource: `/api/locations`,
      uniqOperator: `in`,
      standalone: true,
    } as FilterableColumn,
    {
      attribute: `date`,
      displayName: `Date`,
      type: FilterValueType.rangeDate,
      group: `General`,
      dateOperator: DateRangeOperator.this_month,
      operator: `between`,
      includeToday: true,
    } as FilterableColumn,
    {
      attribute: `status`,
      displayName: `Status`,
      type: FilterValueType.multiple,
      group: `General`,
      options: APPOINTMENT_STATUSES.map((status) => ({ id: status, name: status, value: status })),
    } as FilterableColumn,
    { attribute: `type`, displayName: `Type`, type: FilterValueType.string, group: `General` } as FilterableColumn,
    {
      attribute: `providerId`,
      displayName: `Provider`,
      type: FilterValueType.multiple,
      group: `General`,
      options: providers.options,
    } as FilterableColumn,
    {
      attribute: `procedureCodes`,
      displayName: `Procedure code`,
      type: FilterValueType.string,
      group: `General`,
    } as FilterableColumn,
    { attribute: `amount`, displayName: `Amount`, type: FilterValueType.number, group: `General` } as FilterableColumn,
    {
      attribute: `appointmentMadeDate`,
      displayName: `Booked on`,
      type: FilterValueType.rangeDate,
      group: `General`,
      operator: `between`,
    } as FilterableColumn,
    { attribute: `source`, displayName: `Source`, type: FilterValueType.string, group: `General` } as FilterableColumn,
  ]
  // A pre-applied filter renders as a visible, removable chip, as on Activities.
  return columns.map((column) => ({
    ...column,
    ...addDefaultFilterProperties(column.attribute, preAppliedFilters),
  }))
}
